import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Loader2, CheckCircle2, AlertTriangle, Sparkles, ArrowRight, Lock, Zap, Banknote,
} from 'lucide-react';
import { toast } from 'sonner';
import WithdrawApplicationButton from '../pages/talent/WithdrawApplicationButton';
import GigCodeOfConductModal from './GigCodeOfConductModal';

/**
 * Iter-173 — One-Tap Apply.
 *
 * Mobile-first single-button flow: fetch eligibility on mount, render
 * an instant-apply CTA when eligible, or a "see why" dialog with
 * actionable links when blocked. Surfaces from gig detail, deep links,
 * push notifications, urgent-fill flows.
 *
 * Props:
 *  - shiftId            (required)  — shift uuid
 *  - autoApply          (optional)  — when true and eligible, auto-fires
 *                                      one apply attempt on mount (used
 *                                      from deep links / push)
 *  - compact            (optional)  — render the slim card-row variant
 *  - onApplied          (optional)  — callback after successful apply
 *  - className          (optional)
 */
const OneTapApplyButton = ({
  shiftId, autoApply = false, compact = false, onApplied, onWithdrawn, className = '', backupOnly = false, standbyOnly = false, message = '',
}) => {
  const navigate = useNavigate();
  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showBlockers, setShowBlockers] = useState(false);
  const [showTermsAck, setShowTermsAck] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [autoFired, setAutoFired] = useState(false);
  // 2026-09 — Code of Conduct acceptance is required to apply.
  const [cocVersion, setCocVersion] = useState(null);
  const [cocChecked, setCocChecked] = useState(false);
  const [showCocView, setShowCocView] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/code-of-conduct/current');
        if (!cancelled) setCocVersion(r.data);
      } catch (e) { /* no active policy — apply proceeds without CoC gate */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchEligibility = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/talent/one-tap-apply/${shiftId}/eligibility`);
      setVerdict(r.data);
    } catch (e) {
      // 404 = silent block (client block) or shift gone — render disabled
      setVerdict({ eligible: false, blockers: [], shift: null, gone: true });
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { fetchEligibility(); }, [fetchEligibility]);

  const apply = async () => {
    if (applying) return;
    setApplying(true);
    try {
      const trimmed = (message || '').trim().slice(0, 2000);
      const r = await api.post(
        `/talent/one-tap-apply/${shiftId}`,
        {
          payout_terms_acknowledged: true,
          ...(cocVersion ? { coc_agreed: true, coc_version_id: cocVersion.id } : {}),
          ...(trimmed ? { message_to_client: trimmed } : {}),
        },
      );
      const body = r.data || {};
      setShowTermsAck(false);
      if (body.already_applied) {
        toast.info("You've already applied to this gig.");
      } else {
        toast.success(body.message || "You're applied. We'll notify you when the client decides.");
        if (Array.isArray(body.warnings) && body.warnings.length) {
          body.warnings.slice(0, 1).forEach(w => toast.warning(`Heads up: ${w.message}`));
        }
      }
      // Refresh verdict so the button switches to "Applied".
      await fetchEligibility();
      onApplied?.(body);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && Array.isArray(detail.blockers) && detail.blockers.length) {
        // Re-render verdict with returned blockers so the dialog opens consistently.
        setVerdict(v => ({ ...(v || {}), eligible: false, blockers: detail.blockers }));
        setShowBlockers(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to apply');
      }
    } finally {
      setApplying(false);
    }
  };

  // Auto-apply path (deep links / push). Payout terms still require an
  // explicit acknowledgment, so autoApply opens the terms dialog.
  useEffect(() => {
    if (!autoApply || autoFired || loading || !verdict?.eligible) return;
    setAutoFired(true);
    setTermsChecked(false);
    setCocChecked(false);
    setShowTermsAck(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoApply, autoFired, loading, verdict?.eligible]);

  if (loading) {
    return (
      <Button disabled className={className} data-testid="one-tap-loading">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking eligibility…
      </Button>
    );
  }

  if (!verdict || verdict.gone) {
    return (
      <Button disabled variant="outline" className={className} data-testid="one-tap-gone">
        Gig no longer available
      </Button>
    );
  }

  const blockSeverity = (verdict.blockers || []).filter(b => b.severity === 'block');
  const warnSeverity  = (verdict.blockers || []).filter(b => b.severity === 'warn');

  // Already applied state — offer Withdraw while the application is
  // still pre-booking (pending/accepted). Withdrawing flips the button
  // back to "Apply Now" via a verdict refetch.
  if (verdict.already_applied) {
    const canWithdraw = verdict.application_id
      && ['pending', 'accepted'].includes(verdict.application_status);
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="one-tap-applied-wrap">
        <Button disabled variant="outline"
          className={`bg-cyan-50 border-cyan-300 text-cyan-700 ${className}`}
          data-testid="one-tap-applied">
          <CheckCircle2 className="w-4 h-4 mr-2" /> Applied — we'll notify you
        </Button>
        {canWithdraw && (
          <WithdrawApplicationButton
            applicationId={verdict.application_id}
            onWithdrawn={async () => {
              await fetchEligibility();
              onWithdrawn?.();
            }}
          />
        )}
      </div>
    );
  }

  // Blocked path — show "see why" dialog
  if (!verdict.eligible || blockSeverity.length > 0) {
    return (
      <>
        <Button
          variant="outline"
          onClick={() => setShowBlockers(true)}
          className={`border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 ${className}`}
          data-testid="one-tap-blocked"
        >
          <Lock className="w-4 h-4 mr-2" />
          {blockSeverity.length === 1 ? blockSeverity[0].title : `${blockSeverity.length} things blocking you`}
        </Button>

        <Dialog open={showBlockers} onOpenChange={setShowBlockers}>
          <DialogContent className="bg-white max-w-md" data-testid="one-tap-blockers-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Resolve to apply
              </DialogTitle>
              <DialogDescription>
                Fix the items below and you'll be one tap away from applying.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {blockSeverity.map((b, i) => (
                <div key={i} className="border-l-2 border-amber-400 pl-3"
                     data-testid={`blocker-${b.code}`}>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">
                      Blocker
                    </Badge>
                    <h4 className="text-sm font-semibold text-gray-900">{b.title}</h4>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{b.message}</p>
                  {b.action_url && (
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => { setShowBlockers(false); navigate(b.action_url); }}
                      className="text-cyan-600 px-0 h-auto mt-1"
                      data-testid={`blocker-action-${b.code}`}
                    >
                      {b.action_label || 'Fix this'} <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </div>
              ))}
              {warnSeverity.length > 0 && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">FYI</p>
                  {warnSeverity.map((b, i) => (
                    <div key={i} className="text-xs text-gray-500 mb-1">
                      • <span className="font-medium">{b.title}</span> — {b.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBlockers(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Eligible — render the one-tap CTA. Apply requires acknowledging the
  // gig's payout terms first (V424). FYI warnings render under the CTA.
  const terms = verdict.payout_terms;
  return (
    <div className={compact ? 'inline-flex flex-col gap-1' : 'flex flex-col gap-2'}>
      <Button
        onClick={() => { setTermsChecked(false); setCocChecked(false); setShowTermsAck(true); }}
        disabled={applying}
        className={`bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white shadow-md ${className}`}
        data-testid="one-tap-apply-btn"
      >
        {applying ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</>
        ) : (
          <><Zap className="w-4 h-4 mr-2" /> {standbyOnly ? 'Join Standby List' : backupOnly ? 'Apply as Backup' : 'Apply Now'}</>
        )}
      </Button>
      {warnSeverity.length > 0 && (
        <p className="text-[11px] text-amber-700 leading-tight" data-testid="one-tap-warnings">
          <Sparkles className="w-3 h-3 inline mr-1 -mt-0.5" />
          {warnSeverity[0].message}
        </p>
      )}

      <Dialog open={showTermsAck} onOpenChange={setShowTermsAck}>
        <DialogContent className="bg-white max-w-md" data-testid="payout-terms-ack-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-600" /> Payout terms for this gig
            </DialogTitle>
            <DialogDescription>
              Review how and when you'll be paid before you apply.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-sm font-semibold text-emerald-900" data-testid="payout-terms-days">
                Paid within {terms?.business_days ?? 30} business days
              </p>
              <p className="text-xs text-emerald-800 mt-1" data-testid="payout-terms-disclaimer">
                {terms?.disclaimer || 'Payout is issued after event completion and timesheet approval.'}
              </p>
              {terms?.expedited && (
                <Badge className="mt-2 bg-amber-100 text-amber-800 border border-amber-300" data-testid="payout-terms-critical-pay">
                  <Zap className="w-3 h-3 mr-1" /> {terms.expedited_label}
                </Badge>
              )}
            </div>
            <label className="flex items-start gap-2 cursor-pointer" data-testid="payout-terms-ack-label">
              <input
                type="checkbox"
                checked={termsChecked}
                onChange={(e) => setTermsChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-cyan-600"
                data-testid="payout-terms-ack-checkbox"
              />
              <span className="text-xs text-gray-700">
                I have read and acknowledge the payout terms for this gig, including the
                expected payment timeframe.
              </span>
            </label>
            {cocVersion && (
              <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 space-y-2" data-testid="apply-coc-section">
                <button
                  type="button"
                  onClick={() => setShowCocView(true)}
                  className="text-xs font-semibold text-teal-800 underline"
                  data-testid="apply-coc-review-btn"
                >
                  Review the iEvntür Gig Code of Conduct
                </button>
                <label className="flex items-start gap-2 cursor-pointer" data-testid="apply-coc-ack-label">
                  <input
                    type="checkbox"
                    checked={cocChecked}
                    onChange={(e) => setCocChecked(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-teal-600"
                    data-testid="apply-coc-ack-checkbox"
                  />
                  <span className="text-xs text-gray-700">{cocVersion.checkbox_text}</span>
                </label>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowTermsAck(false)} data-testid="payout-terms-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={apply}
              disabled={!termsChecked || (cocVersion && !cocChecked) || applying}
              className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-white"
              data-testid="payout-terms-confirm-apply-btn"
            >
              {applying ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</>
              ) : (
                'Acknowledge & Apply'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GigCodeOfConductModal
        open={showCocView}
        version={cocVersion}
        readOnly
        heading={cocVersion ? `${cocVersion.title || 'Code of Conduct'}` : undefined}
        onClose={() => setShowCocView(false)}
      />
    </div>
  );
};

export default OneTapApplyButton;
